import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/app';
import { useGameEvents } from '../hooks/useGameEvents';
import { connectSocket } from '../lib/socket';
import {
  isTelegramWebApp,
  getTelegramInitData,
  getTelegramUser,
  getStartParam,
  initTelegramApp
} from '../lib/telegram';
import { usePlatform } from '../platform';
import { webPlatform } from '../platform/web';
import { GameBalanceWidget } from '../components/GameBalanceWidget';

export function HomePage() {
  const [searchParams] = useSearchParams();
  const [joinCode, setJoinCode] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [showPlay, setShowPlay] = useState(searchParams.get('mode') === 'play');
  const navigate = useNavigate();

  const store = useAppStore();
  const { createRoom, joinRoom } = useGameEvents();
  const { platform, displayName: platformName, visibleId, numericId } = usePlatform();

  // Инициализация при загрузке
  useEffect(() => {
    if (isTelegramWebApp()) {
      initTelegramApp();

      const user = getTelegramUser();
      const initData = getTelegramInitData();

      if (user) {
        store.setAuth(
          `tg_${user.id}` as any,
          user.first_name,
          initData
        );

        const startParam = getStartParam();
        connectSocket({
          token: initData,
          role: 'player',
          roomCode: startParam || undefined,
        });

        if (startParam) {
          setJoinCode(startParam);
          setTimeout(() => joinRoom(startParam), 500);
        }
      }
    } else if (platform === 'vk') {
      console.log('[Home] VK platform detected, visibleId:', visibleId, 'numericId:', numericId);
      // VK Mini App
      if (visibleId && numericId) {
        store.setAuth(
          visibleId as any,
          platformName || 'VK User',
          visibleId
        );
        connectSocket({
          token: visibleId,
          role: 'player',
        });
      }
    } else {
      // Web / Guest режим
      const browserId = webPlatform.getBrowserId();
      store.setAuth(
        browserId as any,
        'Гость',
        browserId
      );

      connectSocket({
        token: browserId,
        role: 'player',
      });
    }
  }, [platform, visibleId, numericId]);

  useEffect(() => {
    const code = searchParams.get('join');
    if (code) {
      setJoinCode(code.toUpperCase());
    }
  }, [searchParams]);

  const handleCreate = () => {
    setIsConnecting(true);
    createRoom();
  };

  const handleJoin = (e: React.FormEvent) => {
    e.preventDefault();
    if (joinCode.length !== 4) return;
    setIsConnecting(true);
    joinRoom(joinCode);
  };

  // ── AI-игра (создать/присоединиться) ──
  if (showPlay) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 flex flex-col p-6">
        <button
          onClick={() => setShowPlay(false)}
          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-all mb-4 self-start"
        >
          ← Меню
        </button>
        <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">🎮 AI-игра</h1>
          <p className="text-purple-300">Соло, против ИИ или компанией до 16 чел.</p>
          <p className="text-purple-400 text-sm mt-1">📺 Можно вывести на ТВ</p>
        </div>

        <div className="w-full max-w-sm space-y-4">
          <button
            onClick={handleCreate}
            disabled={isConnecting}
            className="w-full py-4 px-6 bg-white text-indigo-600 font-bold text-lg rounded-2xl
                       shadow-lg hover:shadow-xl transition-all active:scale-95
                       disabled:opacity-50"
          >
            {isConnecting ? 'Подключение...' : 'Создать игру'}
          </button>

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-purple-400/30" />
            <span className="text-purple-300 text-sm">или</span>
            <div className="flex-1 h-px bg-purple-400/30" />
          </div>

          <form onSubmit={handleJoin} className="space-y-3">
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 4))}
              placeholder="Код комнаты"
              maxLength={4}
              className="w-full py-4 px-6 bg-white/10 text-white text-center text-2xl font-bold
                         tracking-widest rounded-2xl border-2 border-white/20
                         placeholder:text-white/40 placeholder:text-base placeholder:tracking-normal
                         focus:outline-none focus:border-white/50"
            />
            <button
              type="submit"
              disabled={joinCode.length !== 4 || isConnecting}
              className="w-full py-4 px-6 bg-white/20 text-white font-bold text-lg rounded-2xl
                         border-2 border-white/30 hover:bg-white/30 transition-all
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Присоединиться
            </button>
          </form>
        </div>

        {store.error && (
          <div className="mt-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-200 text-center">
            {store.error === 'ROOM_NOT_FOUND' && 'Комната не найдена'}
            {store.error === 'ROOM_FULL' && 'Комната заполнена'}
            {store.error === 'GAME_IN_PROGRESS' && 'Игра уже идёт'}
            {!['ROOM_NOT_FOUND', 'ROOM_FULL', 'GAME_IN_PROGRESS'].includes(store.error) && store.error}
          </div>
        )}

        </div>
      </div>
    );
  }

  // ── Главное меню ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 flex flex-col p-6">
      {/* Шапка */}
      <div className="text-center pt-8 pb-6">
        <h1 className="text-5xl font-bold text-white mb-2">🎮 BESHEK</h1>
        <p className="text-purple-300">Викторины с AI!</p>
        {(store.displayName || platformName !== 'Гость') && (
          <p className="text-purple-400 text-sm mt-2">
            Привет, {store.displayName || platformName}!
          </p>
        )}
      </div>

      <div className="max-w-sm mx-auto w-full mb-4">
        <GameBalanceWidget />
      </div>

      <p className="text-purple-300/80 text-center text-sm mb-4">Выбери режим:</p>

      {/* Навигация */}
      <div className="flex-1 flex flex-col gap-3 max-w-sm mx-auto w-full">

        {/* AI-игра */}
        <button
          onClick={() => setShowPlay(true)}
          className="w-full py-5 px-6 bg-gradient-to-r from-orange-500 to-red-500
                     text-white font-bold text-lg rounded-2xl shadow-lg
                     hover:shadow-xl hover:scale-[1.02] transition-all active:scale-95
                     flex items-center gap-4"
        >
          <span className="text-3xl">🎮</span>
          <div className="text-left">
            <div>AI-игра</div>
            <div className="text-sm font-normal opacity-80">Соло или компанией до 16 чел. 📺 ТВ</div>
          </div>
        </button>

        {/* Swipe */}
        <button
          onClick={() => navigate('/swipe')}
          className="w-full py-5 px-6 bg-gradient-to-r from-emerald-500 to-teal-500
                     text-white font-bold text-lg rounded-2xl shadow-lg
                     hover:shadow-xl hover:scale-[1.02] transition-all active:scale-95
                     flex items-center gap-4"
        >
          <span className="text-3xl">⚡</span>
          <div className="text-left">
            <div>Swipe</div>
            <div className="text-sm font-normal opacity-80">Готовые викторины. Streak, XP!</div>
          </div>
        </button>

        {/* Daily Challenge */}
        <button
          onClick={() => navigate('/daily')}
          className="w-full py-5 px-6 bg-gradient-to-r from-amber-500 to-yellow-500
                     text-white font-bold text-lg rounded-2xl shadow-lg
                     hover:shadow-xl hover:scale-[1.02] transition-all active:scale-95
                     flex items-center gap-4"
        >
          <span className="text-3xl">📅</span>
          <div className="text-left">
            <div>Daily Challenge</div>
            <div className="text-sm font-normal opacity-80">Попади в топ дня!</div>
          </div>
        </button>

        {/* Курсы */}
        {/* Курсы - скрыто в VK (правила модерации) */}
        {platform !== 'vk' && (
        <button
          onClick={() => navigate('/collections')}
          className="w-full py-5 px-6 bg-gradient-to-r from-blue-500 to-indigo-500
                     text-white font-bold text-lg rounded-2xl shadow-lg
                     hover:shadow-xl hover:scale-[1.02] transition-all active:scale-95
                     flex items-center gap-4"
        >
          <span className="text-3xl">📚</span>
          <div className="text-left">
            <div>Курсы</div>
            <div className="text-sm font-normal opacity-80">Теория + тесты с сертификатами</div>
          </div>
        </button>
        )}
        {/* Профиль */}
        <button
          onClick={() => navigate('/profile')}
          className="w-full py-4 px-6 bg-white/10
                     text-white font-bold text-lg rounded-2xl
                     border border-white/20 hover:bg-white/20
                     transition-all active:scale-95
                     flex items-center gap-4"
        >
          <span className="text-3xl">👤</span>
          <div className="text-left">
            <div>Профиль</div>
            <div className="text-sm font-normal opacity-60">XP, достижения, сертификаты</div>
          </div>
        </button>

        {/* Поддержка */}
        <button
          onClick={() => {
            window.location.href = '/support';
          }}
          className="w-full py-4 px-6 bg-white/5
                     text-white/70 font-medium rounded-2xl
                     border border-white/10 hover:bg-white/10
                     transition-all active:scale-95
                     flex items-center gap-4"
        >
          <span className="text-2xl">💬</span>
          <div className="text-left">
            <div>Поддержка</div>
          </div>
        </button>
      </div>

      {/* Футер */}
      <div className="text-center py-4 space-y-2">
        <div className="flex justify-center gap-4 text-purple-400/70 text-xs">
          <a href="/pricing" className="hover:text-purple-300">Тарифы</a>
          <span>•</span>
          <a href="/terms" className="hover:text-purple-300">Оферта</a>
          <span>•</span>
          <a href="/refund" className="hover:text-purple-300">Возврат</a>
        </div>
        <div className="text-purple-400/50 text-xs">
          BESHEK v2.9 • {platform === 'vk' ? 'VK' : platform === 'telegram' ? 'Telegram' : 'Web'}
        </div>
      </div>
    </div>
  );
}